/**
 * A lista que não desenha o que ninguém está vendo.
 *
 * O navegador não sofre com o array grande: sofre com o DOM grande. 892 linhas
 * de conversa são 892 retratos, 892 medições de texto com `text-overflow` e
 * 892 caixas a repintar a cada evento do servidor — e a caixa do WhatsApp
 * travava por isso, não por causa do JSON. Aqui só existem no DOM as linhas da
 * janela de leitura, mais uma folga em cima e embaixo para a rolagem não piscar.
 *
 * `@tanstack/react-virtual` e não conta na mão: a altura da linha desta casa é
 * VARIÁVEL (a faixa de etiquetas quebra para a segunda linha quando a tese é
 * longa, `Lista.jsx`), e virtualizar altura variável exige medir cada linha
 * depois de montada e corrigir o deslocamento das que vêm depois. Isso não cabe
 * em algumas linhas, e errar nisso é a lista tremendo ao rolar. Entrou de carona
 * no `@headlessui/react`, que saiu da mesa com a migração para o shadcn — a
 * virtualização ficou porque é dependência declarada, não transitiva.
 *
 * O preço, dito na cara: o Ctrl+F do navegador só acha o que está desenhado.
 * Por isso toda lista longa desta casa tem busca própria, servida pelo servidor
 * — é ela que substitui o Ctrl+F, e é ela que precisa existir ANTES de alguém
 * virtualizar uma lista nova.
 */
import { useCallback, useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { s } from "../visual.jsx";
import { Botao } from "./comuns.jsx";

/* Quantas linhas fora da vista continuam montadas. Oito é o que aguenta um
   giro rápido de roda do mouse sem mostrar buraco branco. */
const FOLGA = 8;

/* A que distância do fim a próxima página é pedida, em linhas. Pedir no último
   item é pedir tarde: a página chega depois de a rolagem ter parado no vazio. */
const ANTECEDENCIA = 12;

/** Identidade padrão de uma linha. Fora do componente: precisa ser estável. */
const PELO_ID = (x) => x.id;

/**
 * @param itens          o que desenhar
 * @param chaveDe        `(item) => key` — precisa ser estável entre páginas
 * @param desenhar       `(item, indice) => JSX` de UMA linha
 * @param alturaEstimada palpite inicial, só até a linha ser medida de verdade
 * @param espaco         o vão entre linhas (o que era `margin-bottom` na linha)
 * @param aoPedirMais    chamado quando a leitura chega perto do fim
 * @param temMais        ainda existe página depois desta
 * @param carregando     um pedido está em voo (evita pedir duas vezes)
 * @param rodape         o que desenhar embaixo: "carregando", erro, "é tudo"
 * @param rotulo         nome acessível da lista
 * @param indiceAlvo     linha que precisa estar à vista (a aberta), ou null
 */
export default function ListaLonga({
  itens,
  chaveDe = PELO_ID,
  desenhar,
  alturaEstimada = 84,
  espaco = 0,
  aoPedirMais,
  temMais = false,
  carregando = false,
  rodape = null,
  rotulo,
  estilo = "",
  indiceAlvo = null,
}) {
  const area = useRef(null);
  const pegarArea = useCallback(() => area.current, []);
  /* Chave do dado, não o índice: sem isto a medida de uma linha fica colada na
     posição e vai para a linha errada quando algo entra na frente dela. */
  const chaveDaLinha = useCallback((i) => chaveDe(itens[i]) ?? i, [itens, chaveDe]);
  const estimar = useCallback(() => alturaEstimada + espaco, [alturaEstimada, espaco]);

  /* oxlint-disable-next-line react/incompatible-library -- o aviso é do React
     Compiler: `useVirtualizer` devolve funções que ele não memoiza. É inerente à
     biblioteca, e o que sai daqui (`linhas`, `getTotalSize`) é consumido no
     próprio render, sem atravessar componente memoizado. */
  const virtual = useVirtualizer({
    count: itens.length,
    getScrollElement: pegarArea,
    estimateSize: estimar,
    overscan: FOLGA,
    getItemKey: chaveDaLinha,
  });

  const linhas = virtual.getVirtualItems();
  const ultimaVista = linhas.length ? linhas[linhas.length - 1].index : -1;

  /* Pedir mais é efeito, não trabalho de render: chamar `aoPedirMais` durante o
     desenho manda o pai mudar de estado no meio do render do filho. */
  useEffect(() => {
    if (temMais && !carregando && ultimaVista >= itens.length - ANTECEDENCIA) aoPedirMais?.();
  }, [temMais, carregando, ultimaVista, itens.length, aoPedirMais]);

  /* A linha aberta vai para a vista quando MUDA — e só então.
     `/atendimentos/300` colado de uma mensagem chegava com a lista no topo e a
     conversa aberta cem linhas abaixo, invisível; a seta que anda entre
     conversas andaria às cegas pelo mesmo motivo. Reagir ao alvo, e não à
     rolagem, é o que evita puxar de volta quem está lendo a lista.
     `align:"auto"` não mexe em nada quando a linha já está desenhada. */
  useEffect(() => {
    if (indiceAlvo == null || indiceAlvo < 0) return;
    virtual.scrollToIndex(indiceAlvo, { align: "auto" });
  }, [indiceAlvo, virtual]);

  return (
    /* Este é o elemento que ROLA, e o virtualizador lê e escreve o `scrollTop`
       dele: continua um `<div>` com `overflow-y`, nunca um `ScrollArea` — que põe
       um viewport interno no meio e desfaz a conta de onde a leitura está. */
    <div ref={area} className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto" style={s(estilo)}>
      <div role="list" aria-label={rotulo} className="relative w-full" style={{ height: virtual.getTotalSize() }}>
        {linhas.map((linha) => (
          <div
            key={linha.key}
            role="listitem"
            aria-setsize={itens.length}
            aria-posinset={linha.index + 1}
            /* `data-index` é o que a biblioteca usa para casar a medida com a
               linha; sem ele a altura medida entra na linha errada. */
            data-index={linha.index}
            ref={virtual.measureElement}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${linha.start}px)`, paddingBottom: espaco }}
          >
            {desenhar(itens[linha.index], linha.index)}
          </div>
        ))}
      </div>
      {rodape}
    </div>
  );
}

/**
 * O rodapé da lista paginada: onde a rolagem diz em que pé está.
 *
 * Existe porque lista infinita sem rodapé mente três vezes: parece que acabou
 * quando só está carregando, parece que acabou quando a rede falhou, e nunca
 * diz quantos existem. Aqui cada um desses estados tem uma frase.
 */
export function RodapeDaPagina({ carregando, erro, temMais, quantos, total, aoTentar, nome = "itens" }) {
  if (erro)
    return (
      <div className="p-4 text-center text-apoio leading-[1.5] text-primary">
        {erro}
        <br />
        {/* O `Botao` de contorno, e não destrutivo nem terra cheio: a falha aqui
            é "a rede oscilou, tente de novo", não estrago — a frase em terra
            acima já é a atenção. Mini (28), porque mora dentro da lista. */}
        <Botao rotulo="Tentar de novo" tamanho="mini" aoTocar={aoTentar} classe="mt-2" />
      </div>
    );
  if (carregando) return <div className="p-4 text-center text-apoio text-muted-foreground">Carregando…</div>;
  if (temMais) return <div className="p-4 text-center text-apoio text-muted-foreground">Role para ver mais</div>;
  if (total != null && quantos > 0)
    return (
      <div className="p-4 text-center font-mono text-selo text-tinta-fraca tabular-nums">
        {total} {nome}
      </div>
    );
  return null;
}
